import { useEffect, useRef, useState } from 'react';
import dropdownArrow from '@/assets/icon/dropdown-arrow.svg';
import { cn } from '@/lib/utils';

interface ComboInputOption {
  label: string;
  value: string;
}

interface ComboInputProps {
  options: ComboInputOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
}

const ComboInput = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
  className,
  name,
}: ComboInputProps) => {
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optValue: string) => {
    setInputValue(optValue);
    onChange(optValue);
    setManualMode(false);
    setOpen(false);
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
  };

  const enterManualMode = () => {
    setManualMode(true);
    setOpen(false);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      {manualMode ? (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            name={name}
            className="input w-full pr-[28px] pl-[10px] py-[6px] text-[14px] h-[36px] rounded-[3px] border border-[var(--neutral-800)] text-[var(--black-500)]"
            placeholder="직접 입력"
            value={inputValue}
            disabled={disabled}
            onChange={handleManualInput}
          />
          <button
            type="button"
            tabIndex={-1}
            className="absolute right-[8px] top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
            onMouseDown={(e) => {
              e.preventDefault();
              setManualMode(false);
              setOpen(!open);
            }}
          >
            <img src={dropdownArrow} alt="" className="w-[12px]" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'w-full text-left cursor-pointer text-[14px] font-normal h-[36px] rounded-[3px] py-[6px] px-[10px]',
            'border border-[var(--neutral-800)] text-[var(--black-500)]',
            'appearance-none bg-no-repeat bg-[97%_50%]',
            'focus:outline-2 focus:outline-[var(--blueprimary-500)]',
            disabled &&
              'bg-[var(--neutral-600)] border-[var(--neutral-600)] text-[#666] cursor-default',
            !inputValue && !disabled && 'text-[var(--neutral-700)]'
          )}
          style={{
            backgroundImage: disabled ? 'none' : `url(${dropdownArrow})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '97% 50%',
          }}
          onClick={() => !disabled && setOpen(!open)}
        >
          {inputValue || placeholder}
        </button>
      )}

      {open && (
        <div className="absolute left-0 right-0 top-full mt-[2px] max-h-[200px] overflow-y-auto bg-[var(--neutral-50)] border border-[var(--neutral-800)] rounded-[3px] shadow-md z-50">
          <button
            type="button"
            className="w-full text-left px-[10px] py-[2px] text-[14px] font-normal text-[var(--black-500)] border-b border-[var(--neutral-800)] hover:bg-[Highlight] hover:text-[HighlightText]"
            onMouseDown={(e) => {
              e.preventDefault();
              enterManualMode();
            }}
          >
            직접 입력...
          </button>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'w-full text-left px-[10px] py-[2px] text-[14px] font-normal text-[var(--black-500)] hover:bg-[Highlight] hover:text-[HighlightText]',
                opt.value === inputValue && 'bg-[var(--neutral-200)]'
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt.value);
              }}
            >
              {opt.value}
            </button>
          ))}
        </div>
      )}

      {!manualMode && <input type="hidden" name={name} value={inputValue} />}
    </div>
  );
};

export default ComboInput;
